import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
    track,
    identifyUser,
    registerSuperProperties,
    resetIdentity,
} from '../utils/mixpanel';
import { AnalyticsEvents } from '../analytics/events';
import { resolveScreen } from '../analytics/screens';

type AnalyticsUser = {
    id: string;
    name?: string | null;
    email?: string | null;
} | null;

type AnalyticsOrg = {
    id: string;
    name?: string | null;
} | null;

type UseAnalyticsArgs = {
    user: AnalyticsUser;
    organization: AnalyticsOrg;
    isStaff?: boolean;
};

/**
 * App-level analytics wiring. Mount once (in AppRoutes) so that:
 *  - every route change emits a "Page Viewed" event with a stable screen name,
 *  - the Mixpanel identity + session super properties follow the signed-in
 *    user, and are cleared on logout.
 *
 * All calls are no-ops when Mixpanel has no token (dev without a token), so
 * this is safe to mount unconditionally.
 */
export function useAnalytics({ user, organization, isStaff }: UseAnalyticsArgs) {
    const location = useLocation();
    const { pathname, search } = location;

    // Identity: identify on the first /me resolution, keep the org super
    // properties current, and reset when the user signs out.
    const identifiedId = useRef<string | null>(null);
    useEffect(() => {
        if (user?.id) {
            if (identifiedId.current !== user.id) {
                identifyUser(user.id, {
                    $name: user.name ?? undefined,
                    $email: user.email ?? undefined,
                });
                identifiedId.current = user.id;
            }
            registerSuperProperties({
                // Stamped onto every event so each can be attributed to a user,
                // in addition to Mixpanel's identify() distinct_id.
                userId: user.id,
                orgId: organization?.id ?? null,
                orgName: organization?.name ?? null,
                isStaff: Boolean(isStaff),
            });
        } else if (identifiedId.current) {
            // Was signed in, now signed out — clear identity + super props.
            resetIdentity();
            identifiedId.current = null;
        }
    }, [user?.id, user?.name, user?.email, organization?.id, organization?.name, isStaff]);

    // Page views: one event per route change.
    useEffect(() => {
        const { screen, pattern } = resolveScreen(pathname);
        track(AnalyticsEvents.PageViewed, {
            screen,
            pattern,
            path: pathname,
            // Only include a search string when present, to avoid a noisy empty prop.
            ...(search ? { search } : {}),
        });
    }, [pathname, search]);
}
